'use client';

import { Option } from '@app/types/data-table';
import { Icons } from '@shared/components/content/icons';
import Loader from '@shared/components/loader';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@shared/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { Separator } from '@shared/components/ui/separator';
import { cn } from '@shared/lib/utils';
import * as React from 'react';

/** An option with an optional secondary line (e.g. an external ID). */
export interface AsyncFilterOption extends Option {
  sublabel?: string;
}

interface AsyncFacetedFilterProps {
  title?: string;
  /**
   * Options for the current search term, served by the backend. The parent
   * owns the query; this component only reports the term via onSearchChange.
   */
  options: AsyncFilterOption[];
  /**
   * The selected options with their labels. Selections carry full options (not
   * just values) because the server-driven option list changes with every
   * search, so selected labels can't be looked up from `options`.
   */
  selected: AsyncFilterOption[];
  onChange: (selected: AsyncFilterOption[]) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  /** Whether an options request is in flight (or pending a debounce). */
  isSearching?: boolean;
  /** Shown when there are no options, e.g. a type-to-search hint. */
  emptyMessage?: string;
  disabled?: boolean;
}

/**
 * FacetedFilter's async twin: same pill trigger and popover, but the option
 * list comes from a backend search instead of client-side filtering — for
 * dimensions with too many values to preload (e.g. purchased goods). While a
 * search is in flight the previous results stay visible under a translucent
 * overlay rather than being displaced.
 */
export function AsyncFacetedFilter({
  title,
  options,
  selected,
  onChange,
  searchTerm,
  onSearchChange,
  isSearching,
  emptyMessage = 'No results found.',
  disabled,
}: AsyncFacetedFilterProps) {
  const [open, setOpen] = React.useState(false);
  const selectedValues = React.useMemo(
    () => new Set(selected.map((option) => option.value)),
    [selected]
  );

  const onItemSelect = React.useCallback(
    (option: AsyncFilterOption, isSelected: boolean) => {
      onChange(
        isSelected
          ? selected.filter((entry) => entry.value !== option.value)
          : [...selected, option]
      );
    },
    [onChange, selected]
  );

  const onReset = React.useCallback(
    (event?: React.MouseEvent) => {
      event?.stopPropagation();
      onChange([]);
    },
    [onChange]
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'border-dashed',
            selectedValues.size > 0 &&
              'border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm hover:bg-primary-600 hover:text-gray-00'
          )}
          disabled={disabled}
        >
          {selectedValues.size > 0 ? (
            <div
              role="button"
              aria-label={`Clear ${title} filter`}
              tabIndex={0}
              onClick={onReset}
              className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Icons.xCircle className="mr-2 size-4" />
            </div>
          ) : (
            <Icons.plusCircle className="mr-2 size-4" />
          )}
          {title}
          {selectedValues.size > 0 && (
            <>
              <Separator
                orientation="vertical"
                className="mx-2 data-[orientation=vertical]:h-4"
              />
              <Badge
                variant="secondary"
                className={cn(
                  'rounded-sm px-1 font-normal lg:hidden',
                  'border-transparent bg-primary-500 text-gray-00'
                )}
              >
                {selectedValues.size}
              </Badge>
              <div className="hidden items-center gap-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge
                    variant="secondary"
                    className={cn(
                      'rounded-sm px-1 font-normal',
                      'border-transparent bg-primary-500 text-gray-00'
                    )}
                  >
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  selected.map((option) => (
                    <Badge
                      variant="secondary"
                      key={option.value}
                      className={cn(
                        'rounded-sm px-1 font-normal',
                        'border-transparent bg-primary-500 text-gray-00'
                      )}
                    >
                      {option.label}
                    </Badge>
                  ))
                )}
              </div>
            </>
          )}
        </Button>
      </PopoverTrigger>
      {/* Fixed width so the popover doesn't resize with each result set;
          long names truncate instead. */}
      <PopoverContent className="w-[22rem] max-w-[90vw] p-0" align="start">
        {/* The backend does the matching; Command must not re-filter. */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={title}
            value={searchTerm}
            onValueChange={onSearchChange}
          />
          <CommandList className="max-h-full">
            <div className="relative">
              {!isSearching && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              <CommandGroup
                className={cn(
                  'max-h-[18.75rem] overflow-y-auto overflow-x-hidden',
                  // Keep a visible area for the overlay when the list is empty.
                  isSearching && options.length === 0 && 'min-h-24'
                )}
              >
                {options.map((option) => {
                  const isSelected = selectedValues.has(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      onSelect={() => onItemSelect(option, isSelected)}
                    >
                      <div
                        className={cn(
                          'flex mr-2 size-4 shrink-0 items-center justify-center rounded-sm border border-primary',
                          isSelected
                            ? 'bg-primary'
                            : 'opacity-50 [&_svg]:invisible'
                        )}
                      >
                        <Icons.check className="text-gray-00" />
                      </div>
                      <div className="flex min-w-0 flex-col">
                        {option.sublabel && (
                          <span className="truncate text-xs text-muted-foreground">
                            {option.sublabel}
                          </span>
                        )}
                        <span className="truncate">{option.label}</span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {/* Translucent overlay: results stay in place while searching. */}
              {isSearching && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-00/60">
                  <Loader size="xs" />
                </div>
              )}
            </div>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onReset()}
                    className="justify-center text-center"
                  >
                    Clear selected
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
